import { CalendarPlusIcon, CheckWavedCircleIcon, StarsTwoIcon } from '@braskai/icons';

import { alpha, css, styled } from '@mui/material';

import { UpsellAnnualOffer, UpsellCategory, UpsellTierOffer } from '@/entities/pricing';

import { AppLocale, useInterfaceLanguage } from '@/shared/i18n';
import { getIntlLocale } from '@/shared/i18n/formatLocale';

import { formatUpsellNumber, formatUpsellPrice, getUpsellPlanName } from '../../lib/upsell/getUpsellCopy';
import lipSyncFrame from './assets/lipSyncFrame.jpg';
import raskMark from './assets/raskMark.svg';
import horizontalPoster from './assets/translate-horizontal-poster.jpg';
import horizontalMp4 from './assets/translate-horizontal.mp4';
import horizontalWebm from './assets/translate-horizontal.webm';
import { SHeroScale } from './styled';
import { UpsellVideo } from './UpsellVideo';

const SCard = styled('div')(
  ({ theme }) => css`
    position: absolute;
    isolation: isolate;
    display: flex;
    flex-direction: column;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 14.5px;
    background-color: ${theme.palette.common.white};
    box-shadow: 0 18px 36px -11px ${alpha(theme.palette.primary[800], 0.4)};
    color: ${theme.palette.grey[900]};
  `,
);

const SCardDecor = styled('span')(
  ({ theme }) => css`
    position: absolute;
    inset: 0;
    z-index: -1;
    overflow: hidden;
    border-radius: inherit;
    background: linear-gradient(
      140deg,
      ${theme.palette.common.white} 35%,
      color-mix(in oklch, ${theme.palette.primary[600]} 6%, ${theme.palette.common.white})
    );
    pointer-events: none;
  `,
);

const SBlob = styled('span')`
  position: absolute;
  top: -98.5px;
  right: -81px;
  width: 215.5px;
  height: 215.5px;
  border-radius: 50%;
  background-color: oklch(0.78 0.12 295);
  opacity: 0.35;
  filter: blur(43px);
`;

const SWatermark = styled('span')(
  ({ theme }) => css`
    position: absolute;
    right: -43px;
    bottom: -57.5px;
    width: 179.5px;
    height: 179.5px;
    background-color: ${theme.palette.primary[600]};
    opacity: 0.07;
    -webkit-mask: url("${raskMark}") center / contain no-repeat;
    mask: url("${raskMark}") center / contain no-repeat;
  `,
);

const NOISE =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>";

const SNoise = styled('span')`
  position: absolute;
  inset: 0;
  background-image: url("${NOISE}");
  background-size: 143.5px;
  opacity: 0.06;
  mix-blend-mode: multiply;
`;

const CardDecor = ({ noise = false }: { noise?: boolean }) => (
  <SCardDecor>
    <SBlob />
    <SWatermark />
    {noise && <SNoise />}
  </SCardDecor>
);

const SBadge = styled('span', { shouldForwardProp: (prop) => !String(prop).startsWith('$') })<{
  $large?: boolean;
  $size: number;
}>(
  ({ $large, $size, theme }) => css`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${$size}px;
    height: ${$size}px;
    border-radius: 50%;
    background-color: ${theme.palette.common.white};
    box-shadow: ${$large
      ? `0 0 0 5.5px ${alpha(theme.palette.common.white, 0.28)}, 0 9px 18px -5.5px ${alpha(theme.palette.primary[800], 0.35)}`
      : `0 0 0 4.5px ${alpha(theme.palette.common.white, 0.35)}, 0 7px 16px -5.5px ${alpha(theme.palette.primary[800], 0.4)}`};
    color: ${theme.palette.primary[500]};
  `,
);

const SPill = styled('span')(
  ({ theme }) => css`
    padding: 2px 7px;
    border-radius: 999px;
    background-color: ${theme.palette.primary[50]};
    color: ${theme.palette.primary[700]};
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    white-space: nowrap;
  `,
);

const TIER_BAR_WIDTH = 475.5;

const STrack = styled('div', { shouldForwardProp: (prop) => !String(prop).startsWith('$') })<{
  $target: boolean;
  $width: number;
}>(
  ({ $target, $width, theme }) => css`
    position: relative;
    width: ${$width}px;
    height: ${$target ? 9 : 3.5}px;
    border-radius: 999px;
    background-color: ${$target ? theme.palette.primary[50] : theme.palette.grey[100]};
  `,
);

const SFill = styled('div', { shouldForwardProp: (prop) => !String(prop).startsWith('$') })<{
  $accent: boolean;
  $width: number;
}>(
  ({ $accent, $width, theme }) => css`
    width: ${$width}px;
    height: 100%;
    border-radius: inherit;
    background: ${$accent
      ? `linear-gradient(90deg, ${theme.palette.primary[600]}, oklch(0.6 0.2 295))`
      : theme.palette.grey[400]};
  `,
);

const SSmall = styled('span')(
  ({ theme }) => css`
    color: ${theme.palette.grey[500]};
    font-size: 11.5px;
    line-height: 16px;
  `,
);

const STierHeader = styled('div')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 7px;
    width: ${TIER_BAR_WIDTH}px;
    font-size: 16px;
    font-weight: 700;
    line-height: 21.5px;

    > svg {
      flex-shrink: 0;
      color: ${theme.palette.primary[600]};
    }
  `,
);

type TierHeroProps = {
  currentCategory: UpsellCategory;
  multiplierLabel: null | string;
  offer: UpsellTierOffer;
  used: number;
};

export const TierHero = ({ currentCategory, multiplierLabel, offer, used }: TierHeroProps) => {
  const { locale, t } = useInterfaceLanguage();
  const { currentMinutes, targetMinutes } = offer;
  const currentWidth = Math.max(24, Math.round((TIER_BAR_WIDTH * currentMinutes) / targetMinutes));
  const currentFill = Math.round((currentWidth * Math.min(used, currentMinutes)) / currentMinutes);
  const targetFill = Math.round((TIER_BAR_WIDTH * Math.min(used, targetMinutes)) / targetMinutes);

  return (
    <SHeroScale aria-hidden="true">
      <SCard sx={{ gap: '23.5px', height: 186.5, left: 63, p: '21.5px 25px', top: 50.5, width: 574.5 }}>
        <CardDecor />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <SSmall>
            {t('upsell.hero.current', {
              minutes: formatUpsellNumber(locale, currentMinutes),
              plan: getUpsellPlanName(currentCategory, t),
            })}
          </SSmall>
          <STrack $target={false} $width={currentWidth}>
            <SFill $accent={false} $width={currentFill} />
          </STrack>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
          <STierHeader>
            <CheckWavedCircleIcon height={18} width={18} />
            {getUpsellPlanName(offer.category, t)}
            {multiplierLabel && <SPill>{multiplierLabel}</SPill>}
            <span style={{ flex: 1 }} />
            {t('upsell.hero.minutes', { minutes: formatUpsellNumber(locale, targetMinutes) })}
          </STierHeader>
          <STrack $width={TIER_BAR_WIDTH} $target>
            <SFill $width={targetFill} $accent />
            <SBadge $size={28.5} style={{ left: TIER_BAR_WIDTH + 11, top: -9.75 }}>
              <StarsTwoIcon height={14.5} width={14.5} />
            </SBadge>
          </STrack>
        </div>
      </SCard>
    </SHeroScale>
  );
};

const MONTH_X = [46.5, 111.5, 168.5, 219];
const MONTH_Y = [81, 131, 181.5];
const MONTH_OPACITY = [0.6, 0.75, 0.88, 1];

const SMonth = styled('span', { shouldForwardProp: (prop) => !String(prop).startsWith('$') })<{ $fill: null | number }>(
  ({ $fill, theme }) => css`
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 46.5px;
    height: 36px;
    border: ${$fill === null ? `1px solid ${theme.palette.grey[200]}` : `1.35px solid ${theme.palette.primary[500]}`};
    border-radius: 9px;
    background: ${$fill === null
      ? theme.palette.common.white
      : `linear-gradient(to top, ${theme.palette.primary[100]} ${$fill}%, ${theme.palette.common.white} ${$fill}%)`};
    box-shadow: 0 5.5px 12.5px -5.5px ${alpha(theme.palette.primary[800], 0.35)};
    color: ${theme.palette.grey[$fill === null ? 700 : 900]};
    font-size: 11.5px;
    font-weight: 600;
    line-height: 16px;
  `,
);

const SPlanLabel = styled('span')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 7px;
    color: ${theme.palette.grey[700]};
    font-size: 11.5px;
    font-weight: 500;
    line-height: 16px;

    > svg {
      flex-shrink: 0;
      color: ${theme.palette.grey[600]};
    }
  `,
);

const SBigNumber = styled('span')`
  font-size: 36px;
  font-weight: 700;
  line-height: 41.5px;
  letter-spacing: -0.01em;
`;

const SAvailable = styled('span')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 7px;
    color: ${theme.palette.grey[600]};
    font-size: 12.5px;
    font-weight: 500;
    line-height: 18px;

    &::before {
      content: '';
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background-color: ${theme.palette.success[500]};
    }
  `,
);

const SSavePill = styled(SPill)`
  padding: 2.5px 9px;
`;

const getMonthLabels = (locale: AppLocale) => {
  const format = new Intl.DateTimeFormat(getIntlLocale(locale), { month: 'short' });

  return Array.from({ length: 12 }, (_, month) => ({
    label: format.format(new Date(2026, month, 1)).replace('.', ''),
    month,
  }));
};

type AnnualHeroProps = {
  offer: UpsellAnnualOffer;
  usagePercent?: number;
};

export const AnnualHero = ({ offer, usagePercent = 50 }: AnnualHeroProps) => {
  const { locale, t } = useInterfaceLanguage();
  const currentMonth = new Date().getMonth();
  const months = getMonthLabels(locale);

  return (
    <SHeroScale aria-hidden="true">
      {months.map(({ label, month: index }) => {
        const isCurrent = index === currentMonth;

        return (
          <SMonth
            key={index}
            style={{
              left: MONTH_X[index % 4],
              opacity: isCurrent ? 1 : MONTH_OPACITY[index % 4],
              top: MONTH_Y[Math.floor(index / 4)],
            }}
            $fill={isCurrent ? usagePercent : null}
          >
            {label}
          </SMonth>
        );
      })}
      <SCard sx={{ height: 186.5, justifyContent: 'space-between', left: 305, p: '20px 23.5px', top: 50.5, width: 323 }}>
        <CardDecor noise />
        <SBadge $size={32.5} style={{ left: -14.5, top: -14.5 }}>
          <StarsTwoIcon height={16} width={16} />
        </SBadge>
        <SPlanLabel>
          <CalendarPlusIcon height={16} width={16} />
          {t('upsell.hero.annualPlan', { plan: getUpsellPlanName(offer.category, t) })}
        </SPlanLabel>
        <span style={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          <SBigNumber>{t('upsell.hero.minutes', { minutes: formatUpsellNumber(locale, offer.pool) })}</SBigNumber>
          <SAvailable>{t('upsell.hero.fullYear')}</SAvailable>
        </span>
        <span>
          <SSavePill>
            {t('upsell.hero.savePerYear', { amount: formatUpsellPrice(locale, offer.savings, offer.currency) })}
          </SSavePill>
        </span>
      </SCard>
    </SHeroScale>
  );
};

const SFrame = styled('div')(
  ({ theme }) => css`
    position: absolute;
    top: 41.5px;
    left: 170.5px;
    width: 359px;
    height: 204.5px;
    overflow: hidden;
    border: 3.5px solid ${theme.palette.common.white};
    border-radius: 14.5px;
    background-color: ${theme.palette.common.white};
    box-shadow: 0 18px 36px -11px ${alpha(theme.palette.primary[800], 0.4)};

    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      left: 50%;
      width: 2px;
      margin-left: -1px;
      background-color: ${theme.palette.common.white};
    }
  `,
);

const SSplitHandle = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3.5px;
    width: 27px;
    height: 27px;
    margin: -13.5px 0 0 -13.5px;
    border-radius: 50%;
    background-color: ${theme.palette.common.white};
    box-shadow: 0 3.5px 9px ${alpha(theme.palette.grey[900], 0.25)};

    &::before,
    &::after {
      content: '';
      width: 2px;
      height: 11px;
      border-radius: 2px;
      background-color: ${theme.palette.grey[400]};
    }
  `,
);

const SFrameTag = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 9px;
    right: 9px;
    z-index: 1;
    padding: 2px 6.5px;
    border-radius: 5.5px;
    background-color: ${theme.palette.common.white};
    box-shadow: 0 1px 2px ${alpha(theme.palette.grey[900], 0.08)};
    color: ${theme.palette.grey[900]};
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
  `,
);

const SSrtCard = styled('span')(
  ({ theme }) => css`
    position: absolute;
    top: 70px;
    left: 466.5px;
    display: flex;
    flex-direction: column;
    gap: 7px;
    width: 100.5px;
    height: 125.5px;
    padding: 11px 11px 11px 30.5px;
    border: 1px solid ${theme.palette.grey[200]};
    border-radius: 11px;
    background-color: ${theme.palette.common.white};
    box-shadow: 0 11px 21.5px -7px ${alpha(theme.palette.primary[800], 0.35)};
    transform: rotate(6deg);
  `,
);

const SSrtPill = styled('span')(
  ({ theme }) => css`
    align-self: flex-end;
    padding: 1px 5.5px;
    border-radius: 4.5px;
    background-color: ${theme.palette.grey[100]};
    color: ${theme.palette.grey[900]};
    font-size: 10px;
    font-weight: 600;
    line-height: 14.5px;
  `,
);

const SSrtLine = styled('span')(
  ({ theme }) => css`
    height: 5.5px;
    border-radius: 999px;
    background-color: ${theme.palette.grey[200]};
  `,
);

const SRT_LINES = [
  { id: 'first', width: '100%' },
  { id: 'second', width: '70%' },
  { id: 'third', width: '100%' },
  { id: 'fourth', width: '55%' },
];

const HeroVideo = () => <UpsellVideo mp4={horizontalMp4} poster={horizontalPoster} webm={horizontalWebm} />;

const SFaceImage = styled('img')`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const LipSyncHero = () => {
  const { t } = useInterfaceLanguage();

  return (
    <SHeroScale aria-hidden="true">
      <SSrtCard>
        <SSrtPill>SRT</SSrtPill>
        {SRT_LINES.map(({ id, width }) => (
          <SSrtLine key={id} style={{ width }} />
        ))}
      </SSrtCard>
      <SFrame>
        <SFaceImage alt="" src={lipSyncFrame} />
        <SFrameTag>{t('upsell.hero.lipSync')}</SFrameTag>
        <SSplitHandle />
      </SFrame>
      <SBadge $size={39.5} style={{ left: 149, top: 21.5 }} $large>
        <StarsTwoIcon height={20} width={20} />
      </SBadge>
    </SHeroScale>
  );
};

export const VideoHero = () => (
  <div aria-hidden="true" style={{ backgroundColor: 'white', inset: 0, position: 'absolute' }}>
    <HeroVideo />
  </div>
);
